import React from 'react';
import { Card, Table, type TableProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';

export interface DataTableRef {
  exportCSV: (opts?: { filename?: string; rows?: any[]; excludeKeys?: string[]; exportGroupHeaders?: GroupHeaderConfig[] }) => void;
}

export interface GroupHeaderConfig {
  title: React.ReactNode;
  children: Array<string | GroupHeaderConfig>;
  fixed?: 'left' | 'right';
}

export interface DataTableProps<T> extends Omit<TableProps<T>, 'columns' | 'dataSource' | 'rowKey'> {
  columns: ColumnsType<T>;
  dataSource: T[];
  rowKey?: string | ((record: T) => string);
  groupHeaders?: GroupHeaderConfig[];
}

const DataTable = React.forwardRef<DataTableRef, DataTableProps<any>>((props, ref) => {
  const { columns, dataSource, rowKey, groupHeaders, ...rest } = props;

  const getColKey = (c: any): string | undefined => {
    if (c?.key) return String(c.key);
    if (typeof c?.dataIndex === 'string') return c.dataIndex as string;
    return undefined;
  };

  const leafColumns = (cols: any[]): any[] => {
    const out: any[] = [];
    cols.forEach(c => {
      if (Array.isArray(c.children) && c.children.length) out.push(...leafColumns(c.children));
      else out.push(c);
    });
    return out;
  };

  const calcScrollX = (cols: any[]): number => {
    const leaves = leafColumns(cols);
    const total = leaves.reduce((sum, c) => sum + (Number(c.width) || 120), 0);
    return total;
  };

  const buildGroupedColumns = (base: any[], groups?: GroupHeaderConfig[]): any[] => {
    if (!groups || !groups.length) return base;
    const keyOf = (c: any) => getColKey(c);
    const keyOrder: (string | undefined)[] = base.map(keyOf);
    const colByKey = new Map<string, any>();
    base.forEach(c => { const k = keyOf(c); if (k) colByKey.set(k, c); });

    const collectGroup = (g: GroupHeaderConfig): { node: any; usedKeys: string[] } | null => {
      const used: string[] = [];
      const children: any[] = g.children.map(ch => {
        if (typeof ch === 'string') {
          const col = colByKey.get(ch);
          if (col) used.push(ch);
          return col;
        } else {
          const sub = collectGroup(ch);
          if (sub) { used.push(...sub.usedKeys); return sub.node; }
          return null;
        }
      }).filter(Boolean);
      if (!children.length) return null;
      return { node: { title: g.title, children, fixed: g.fixed }, usedKeys: Array.from(new Set(used)) };
    };

    const groupsInfo = groups.map(collectGroup).filter(Boolean) as { node: any; usedKeys: string[] }[];
    if (!groupsInfo.length) return base;

    const indexOfKey = (k: string): number => {
      for (let i = 0; i < keyOrder.length; i++) if (keyOrder[i] === k) return i;
      return -1;
    };

    const removedKeys = new Set<string>();
    const groupsAtIndex = new Map<number, any[]>();
    groupsInfo.forEach(info => {
      const idxs = info.usedKeys.map(indexOfKey).filter(i => i >= 0);
      if (!idxs.length) return;
      const firstIdx = Math.min(...idxs);
      info.usedKeys.forEach(k => removedKeys.add(k));
      const arr = groupsAtIndex.get(firstIdx) || [];
      arr.push(info.node);
      groupsAtIndex.set(firstIdx, arr);
    });

    const result: any[] = [];
    for (let i = 0; i < base.length; i++) {
      const insertGroups = groupsAtIndex.get(i);
      if (insertGroups && insertGroups.length) {
        insertGroups.forEach(node => result.push(node));
      }
      const k = keyOrder[i];
      if (!k || !removedKeys.has(k)) result.push(base[i]);
    }
    return result;
  };

  const resolvedColumns = buildGroupedColumns((Array.isArray(columns) ? columns : []) as any, groupHeaders);

  const handleExport = (filename?: string, exportRows?: any[], excludeKeys?: string[], exportGroupHeaders?: GroupHeaderConfig[]) => {
    const toText = (node: any): string => {
      if (node === null || node === undefined || typeof node === 'boolean') return '';
      if (typeof node === 'string' || typeof node === 'number') return String(node);
      if (Array.isArray(node)) return node.map(toText).join('');
      if (React.isValidElement(node)) return toText((node as any).props?.children);
      if (typeof node === 'object') {
        const children = (node as any)?.children;
        if (children !== undefined) return toText(children);
      }
      return String(node);
    };

    const excludedKeySet = new Set((excludeKeys || []).map((key) => String(key)));
    const leafCols = leafColumns(resolvedColumns as any[]).filter((col) => {
      const colKey = getColKey(col);
      if (!colKey) return true;
      return !excludedKeySet.has(String(colKey));
    });

    const keyOf = (c: any) => (typeof c?.dataIndex === 'string' ? c.dataIndex : String(c?.key ?? ''));

    const collectKeys = (g: GroupHeaderConfig): string[] => g.children.flatMap((ch) =>
      typeof ch === 'string' ? [ch] : collectKeys(ch)
    );

    const effectiveGroupHeaders = Array.isArray(exportGroupHeaders) ? exportGroupHeaders : groupHeaders;
    const groupTitleByKey: Record<string, string> = {};
    if (Array.isArray(effectiveGroupHeaders)) {
      effectiveGroupHeaders.forEach(g => {
        const keys = collectKeys(g);
        keys.forEach(k => { groupTitleByKey[k] = toText(g.title); });
      });
    }

    const headerTop = Array.isArray(effectiveGroupHeaders) && effectiveGroupHeaders.length > 0
      ? leafCols.map(c => groupTitleByKey[keyOf(c)] ?? '')
      : null;
    const headerBottom = leafCols.map(c => toText(c.title ?? c.key ?? ''));

    const headers = headerTop ? [headerTop.join(','), headerBottom.join(',')].join('\n') : headerBottom.join(',');

    const sourceRows = Array.isArray(exportRows) ? exportRows : (Array.isArray(dataSource) ? dataSource : []);
    const rows = sourceRows.map((record: any) => {
      return leafCols.map(c => {
        const idx = c.dataIndex;
        let val = idx !== undefined ? record[idx] : '';
        if (typeof c.render === 'function') {
          const rendered = c.render(val, record);
          val = toText(rendered);
        }
        const s = toText(val);
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      }).join(',');
    }).join('\n');

    const csv = headers + '\n' + rows;
    const utf8Bom = '\uFEFF';
    const blob = new Blob([utf8Bom, csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'table-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  React.useImperativeHandle(ref, () => ({
    exportCSV: (opts?: { filename?: string; rows?: any[]; excludeKeys?: string[]; exportGroupHeaders?: GroupHeaderConfig[] }) => handleExport(opts?.filename, opts?.rows, opts?.excludeKeys, opts?.exportGroupHeaders)
  }));

  return (
    <Table
      bordered
      size="small"
      pagination={false}
      {...rest}
      columns={resolvedColumns as any}
      dataSource={(Array.isArray(dataSource) ? dataSource : []) as any}
      rowKey={(rowKey as any) || ((r: any, i: number) => r?.id ?? r?.key ?? String(i))}
      scroll={{ x: calcScrollX(resolvedColumns as any), ...rest.scroll }}
    />
  );
});

export default DataTable;
